import { useEffect, useState, useRef } from "react";
import {
    checkAudioPermissionStatus,
    checkAudioPermissionStatusWithSetter,
} from "../../permissions";
import { useTranslation } from "react-i18next";
import AudioPermissionCard from "./AudioPermissionCard";
import { startRecording } from "../../recorder";
import { useAudio } from "../../contexts/Audio";
import { analyzeSpeech } from "../../pronunciation";

const RecordingButton = ({
    currentlyRecording,
    setCurrentlyRecording,
    setRecordingState,
    text,
    selectedLanguage,
    setCeleryTaskId,
    setAudioDuration,
    setRecordingBlob,
    setTextareaHasError,
    skipPermissionCard = false,
    originUrl = null,
}) => {
    const [permissionState, setPermissionState] = useState("checking"); // checking, prompt, denied, granted, unknown
    const { t } = useTranslation();
    const [showAudioPermissionCard, setShowAudioPermissionCard] = useState(false);
    const [audioPermissionIsDenied, setAudioPermissionIsDenied] = useState(false);
    const recorderRef = useRef(null);
    const { startAudioContext, stopAudioContext, setIsRecording } = useAudio();

    const onCloseAudioPermissionCard = async (gavePermission) => {
        if (gavePermission) {
            try {
                recorderRef.current = await startRecording(
                    {
                        onStreamReady: (stream) => {
                            startAudioContext(stream);
                        },
                    },
                    50,
                );
                setShowAudioPermissionCard(false);
                setTimeout(() => {
                    setCurrentlyRecording(true);
                    setIsRecording(true);
                    setRecordingState("recording");
                }, 100);
            } catch (error) {
                // user likely didn't give permission
                setPermissionState(await checkAudioPermissionStatus());
                recorderRef.current = null;
                setCurrentlyRecording(false);
                setIsRecording(false);
            }

            return;
            //try {
            //    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            //    stream.getTracks().forEach((track) => track.stop());
            //} catch (error) {
            //   console.log(error);
            //    console.log(await checkAudioPermissionStatus());
            //}
            //setPermissionState(await checkAudioPermissionStatus());
            // prompt audio request
            //setShowAudioPermissionCard(false);
        } else {
            setShowAudioPermissionCard(false);
        }
    };

    useEffect(() => {
        checkAudioPermissionStatusWithSetter(setPermissionState, setAudioPermissionIsDenied);
    }, []);

    useEffect(() => {
        setAudioPermissionIsDenied(permissionState === "denied");
    }, [permissionState]);

    const handleClick = async () => {
        if (text.trim().length === 0) {
            setTextareaHasError(true);
            return;
        }

        const permission = await checkAudioPermissionStatus();
        setPermissionState(permission);
        if (currentlyRecording) {
            // STOP logic
            try {
                if (recorderRef.current) {
                    const result = await recorderRef.current.stop();
                    setAudioDuration(result.durationMs);
                    setRecordingBlob(result.blob);
                    setRecordingState("analyzing");
                    const taskId = await analyzeSpeech(
                        result.blob,
                        recorderRef.current.mimeType,
                        text,
                        selectedLanguage,
                        originUrl,
                    );
                    setCeleryTaskId(taskId.task_id);
                }
            } catch (error) {
                console.error("Failed to analyze speech:", error);
                alert(t("alert.analysis-failed"));
                setRecordingState("idle");
            } finally {
                recorderRef.current = null;
                setCurrentlyRecording(false);
                setIsRecording(false);
                stopAudioContext();
            }
            return;
        }
        switch (permission) {
            case "granted":
                try {
                    setCurrentlyRecording(true);
                    setIsRecording(true);
                    setRecordingState("recording");

                    recorderRef.current = await startRecording(
                        {
                            onStreamReady: (stream) => {
                                startAudioContext(stream);
                            },
                        },
                        50,
                    );
                } catch (error) {
                    // user likely didn't give permission
                    setPermissionState(await checkAudioPermissionStatus());
                    recorderRef.current = null;
                    setCurrentlyRecording(false);
                    setIsRecording(false);
                    setRecordingState("idle");
                }
                break;
            case "denied":
                if (skipPermissionCard) {
                    alert(t("alert.browser-cannot-record"));
                } else {
                    setAudioPermissionIsDenied(true);
                    setShowAudioPermissionCard(true);
                }
                break;
            case "prompt":
                if (skipPermissionCard) {
                    // Try recording directly — browser will show its native permission prompt
                    try {
                        setCurrentlyRecording(true);
                        setIsRecording(true);
                        setRecordingState("recording");
                        recorderRef.current = await startRecording(
                            {
                                onStreamReady: (stream) => {
                                    startAudioContext(stream);
                                },
                            },
                            50,
                        );
                    } catch (error) {
                        setPermissionState(await checkAudioPermissionStatus());
                        recorderRef.current = null;
                        setCurrentlyRecording(false);
                        setIsRecording(false);
                        setRecordingState("idle");
                        alert(t("alert.browser-cannot-record"));
                    }
                } else {
                    setShowAudioPermissionCard(true);
                }
                break;
            default:
                alert(t("alert.browser-cannot-record"));
        }
    };

    const handleCancelRecording = async () => {
        setCurrentlyRecording(false);
        setIsRecording(false);
        recorderRef.current.cancel();
        recorderRef.current = null;
        stopAudioContext();
        setRecordingState("idle");
    };

    return (
        <>
            {!skipPermissionCard && (
                <AudioPermissionCard
                    showAudioPermissionCard={showAudioPermissionCard}
                    onCloseAudioPermissionCard={onCloseAudioPermissionCard}
                    denied={audioPermissionIsDenied}
                />
            )}
            <div className="flex items-center justify-center flex-col z-10">
                <button
                    className="bg-primary hover:bg-purple-500 shadow-[0_0_10px_1px_rgba(128,90,213,0.6)] hover:shadow-[0_0_10px_3px_rgba(128,90,213,0.6)] text-white rounded-full w-32 md:w-36 h-32 md:h-36 transition-shadow border-purple-500 border"
                    onPointerDown={handleClick}
                >
                    <div className="flex justify-center">
                        {currentlyRecording ? (
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                fill="currentColor"
                                className="size-14 md:size-16"
                            >
                                <path
                                    fillRule="evenodd"
                                    d="M4.5 7.5a3 3 0 0 1 3-3h9a3 3 0 0 1 3 3v9a3 3 0 0 1-3 3h-9a3 3 0 0 1-3-3v-9Z"
                                    clipRule="evenodd"
                                />
                            </svg>
                        ) : (
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                fill="currentColor"
                                className="size-14 md:size-16"
                            >
                                <path d="M8.25 4.5a3.75 3.75 0 1 1 7.5 0v8.25a3.75 3.75 0 1 1-7.5 0V4.5Z" />
                                <path d="M6 10.5a.75.75 0 0 1 .75.75v1.5a5.25 5.25 0 1 0 10.5 0v-1.5a.75.75 0 0 1 1.5 0v1.5a6.751 6.751 0 0 1-6 6.709v2.291h3a.75.75 0 0 1 0 1.5h-7.5a.75.75 0 0 1 0-1.5h3v-2.291a6.751 6.751 0 0 1-6-6.709v-1.5A.75.75 0 0 1 6 10.5Z" />
                            </svg>
                        )}
                    </div>
                </button>
                <button
                    className={`border border-border px-2 py-1 rounded-md mt-3 text-gray-700 hover:bg-gray-100${
                        currentlyRecording ? "" : " hidden"
                    }`}
                    onClick={handleCancelRecording}
                >
                    {t("cancel")}
                </button>
            </div>
        </>
    );
};

export default RecordingButton;
